import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import Authorize from '../../components/AuthComponent/Authorize';
import SaveButton from '../../components/Buttons/SaveButton';
import ButtonBar from '../../components/Fields/ButtonBar';
import DateControl from '../../components/Fields/DateControl';
import Field from '../../components/Fields/Field';
import FieldLabel from '../../components/Fields/FieldLabel';
import Form from '../../components/Fields/Form';
import MultiSelectField from '../../components/Fields/MultiSelectField';
import NumberField from '../../components/Fields/NumberField';
import SelectListField from '../../components/Fields/SelectListField';
import SwitchField from '../../components/Fields/SwitchField';
import TextboxField from '../../components/Fields/TextboxField';
import { FormConfig } from '../../components/Fields/Types';
import useSelectListOptions from '../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import { HorizontalTabs } from '../../components/Tabs/HorizontalTabs';
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import AttachmentTypeModel from '../../models/AttachmentTypeModel';
import DataService from '../../services/data.service';
import IdParams from '../../types/IdParams';
import { DataResult } from '../../types/Result';
import FormatUtils from '../../utils/FormatUtils';
import Unauthorized from '../Authentication/Unauthorized';
import AttachmentType_tab_DocumentTemplates from './Tabs/AttachmentType_tab_DocumentTemplates';


const AttachmentTypeView: React.FC = () => {
    const dataSourceTypes = useSelectListOptions('api/v1/selectlist/DataSourceTypes', '-- Select datasource type');
    const businessPartnerTypes = useSelectListOptions('api/v1/selectlist/BusinessPartnerTypes');
    const itemTypes = useSelectListOptions('api/v1/selectlist/ItemTypes');
    const projectTypes = useSelectListOptions('api/v1/selectlist/ProjectTypes');
    const [serverErrors, setServerErrors] = useState<DataResult<AttachmentTypeModel>>({ isSuccesful: true, messages: [], data: undefined });
    const { handleResult, handleException } = useNotifications();
    const [isAuthorized, setIsAuthorized] = useState<boolean>(true);

    const [formData, setFormData] = useState<AttachmentTypeModel>({
        id: 0,
        name: '',
        isGeneratable: false,
        forLicense: false,
        forUser: false,
        forBom: false,
        beginDate: new FormatUtils().formatDateForStorage(new Date()),
        endDate: '',
        canDelete: true,
        isSearchable: true,
        businessPartnerTypeIds: [],
        itemTypeIds: [],
        projectTypeIds: [],
        concurrencyId: ''
    });

    let idParams: IdParams = useParams();
    let attachmentTypeId: number | undefined;
    if (idParams.id && !Number.isNaN(parseInt(idParams.id))) {
        attachmentTypeId = parseInt(idParams.id);
    }

    useEffect(() => {
        if (attachmentTypeId !== undefined) {
            DataService.getById<AttachmentTypeModel>('api/v1/attachmentTypes/', attachmentTypeId)
                .then(response => setFormData(response))
                .catch(e => {
                    if (e.response?.data?.status === 401)
                        setIsAuthorized(false);
                    else
                        handleException(e);
                });
        }
    }, [attachmentTypeId]);

    function submit() {
        if (formData) {
            DataService
                .update<AttachmentTypeModel>(`api/v1/attachmentTypes/`, formData)
                .then(response => {
                    handleResult(response);
                    setServerErrors(response);
                    if (response.isSuccesful === true && response.data)
                        setFormData(response.data);
                })
                .catch(e => handleException(e));
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'name', label: 'Name', validationRules: [{ type: 'isRequired' }] },
            { name: 'abbreviation', label: 'Abbreviation', validationRules: [{ type: 'isMaxLength', maxLength: 10 }] },
            { name: 'maxAttachments', label: 'MaxAttachments' },
            { name: 'imagePriority', label: 'Image priority' },
            { name: 'dataSourceTypeId', label: 'Datasource' },
            { name: 'businessPartnerTypeIds', label: 'Business-partner types' },
            { name: 'itemTypeIds', label: 'Item types' },
            { name: 'projectTypeIds', label: 'Project types' },
            { name: 'beginDate', label: 'Active from', validationRules: [{ type: 'isRequired' }, { type: 'isDate' }] },
            { name: 'endDate', label: 'Until', labelSize: 'auto', validationRules: [{ type: 'isDate' }] },

            { name: 'isGeneratable', label: 'Generatable', labelSize: 4 },
            { name: 'isSearchable', label: 'Searchable', labelSize: 4 },
            { name: 'canDelete', label: 'Attachment can be deleted', labelSize: 4 },
            { name: 'forLicense', label: 'For licenses', labelSize: 4 },
            { name: 'forUser', label: 'For users', labelSize: 4 },
            { name: 'forBom', label: 'For Bill-of-Materials', labelSize: 4 },

        ],
        handleSubmit: submit,
        showErrors: 'always',
    };

    const { getLabelProps, getControlProps, getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    const { isAuthorizedFor } = useAuth();
    if (attachmentTypeId === undefined) {
        if (!isAuthorizedFor(ApplicationFunctions.EditAttachmentTypes)) {
            return (<Unauthorized />);
        }
    }
    else if (!isAuthorized) {
        return (<Unauthorized />);
    }

    return (
        <div className='main-content'>
            <Form title='Attachment type' footer={(
                <Row>
                    <Col md='12'>
                        <ButtonBar offset={2}>
                            <Authorize applicationFunction={ApplicationFunctions.EditBrand}>
                                <SaveButton  {...getSubmitButtonProps()} />
                            </Authorize>
                        </ButtonBar>
                    </Col>
                </Row>
            )}>
                <Container fluid>
                    <Row>
                        <Col md='6'>
                            <TextboxField {...getFieldProps('name')} />
                            <TextboxField {...getFieldProps('abbreviation')} />
                            <NumberField  {...getFieldProps('maxAttachments')} />
                            <NumberField  {...getFieldProps('imagePriority')} />
                            <SelectListField {...getFieldProps('dataSourceTypeId')} options={dataSourceTypes} />
                            <MultiSelectField {...getFieldProps('itemTypeIds')} options={itemTypes} />
                            <MultiSelectField {...getFieldProps('projectTypeIds')} options={projectTypes} />
                            <MultiSelectField {...getFieldProps('businessPartnerTypeIds')} options={businessPartnerTypes} />
                            <Field {...getLabelProps('beginDate')}>
                                <DateControl {...getControlProps('beginDate')} />
                                <span style={{ padding: '7px 15px' }}><FieldLabel {...getLabelProps('endDate')} /></span>
                                <DateControl {...getControlProps('endDate')} />
                            </Field>
                        </Col>
                        <Col md='6'>
                            <SwitchField {...getFieldProps('isGeneratable')} />
                            <SwitchField {...getFieldProps('isSearchable')} />
                            <SwitchField {...getFieldProps('canDelete')} />
                            <SwitchField {...getFieldProps('forLicense')} />
                            <SwitchField {...getFieldProps('forUser')} />
                            <SwitchField {...getFieldProps('forBom')} />
                        </Col>

                    </Row>
                </Container>
            </Form>
            {attachmentTypeId && (
                <Container fluid>
                    <Row>
                        <Col md='12'>
                            <HorizontalTabs
                                id='vtabs'
                                defaultActiveKey='documentTemplates'
                                tabs={[
                                    {
                                        title: 'Document templates',
                                        eventKey: 'documentTemplates',
                                        component: <AttachmentType_tab_DocumentTemplates attachmentTypeId={attachmentTypeId} />,
                                        applicationFunction: ApplicationFunctions.ViewDocumentTemplates
                                    },
                                ]}
                            />
                        </Col>
                    </Row>
                </Container>
            )}
        </div>
    );
}
export default AttachmentTypeView;